import React, { useEffect, useState } from 'react';
import BaseDrawer from './BaseDrawer';
import { useAdminData } from '../../contexts/AdminDataContext';
import { Building2, Mail, Globe, MapPin, Gift, Users } from 'lucide-react';
import { allCategories } from '../../utils/partnersData';
interface PartnerDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  partnerId: string | null;
}
const PartnerDetailDrawer: React.FC<PartnerDetailDrawerProps> = ({
  isOpen,
  onClose,
  partnerId
}) => {
  const {
    partners,
    updatePartner
  } = useAdminData();
  const partner = partners.find(p => p.id === partnerId);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    website: '',
    description: '',
    locations: 0,
    categories: [] as string[],
    status: 'active' as 'active' | 'inactive' | 'pending'
  });
  useEffect(() => {
    if (partner) {
      setFormData({
        name: partner.name,
        email: partner.email,
        website: partner.website,
        description: partner.description,
        locations: partner.locations,
        categories: partner.categories,
        status: partner.status
      });
    }
  }, [partner]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'locations' ? parseInt(value) || 0 : value
    });
  };
  const handleCategoryToggle = (category: string) => {
    setFormData({
      ...formData,
      categories: formData.categories.includes(category) ? formData.categories.filter(c => c !== category) : [...formData.categories, category]
    });
  };
  const handleSave = () => {
    if (partnerId) {
      updatePartner(partnerId, formData);
      onClose();
    }
  };
  if (!partner) return null;
  return <BaseDrawer isOpen={isOpen} onClose={onClose} title="Dettagli Partner" description={`ID: ${partner.id}`} width="lg" footer={<div className="flex gap-3" data-id="element-697">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-698">
            Annulla
          </button>
          <button onClick={handleSave} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors" data-id="element-699">
            Salva modifiche
          </button>
        </div>} data-id="element-696">
      {/* Logo and Name */}
      <div className="flex items-center gap-4" data-id="element-700">
        <div className="h-20 w-20 rounded-lg border border-gray-200 bg-white flex items-center justify-center p-3 flex-shrink-0" data-id="element-701">
          <img src={partner.logo} alt={partner.name} className="max-h-full max-w-full object-contain" onError={e => {
          e.currentTarget.src = 'https://via.placeholder.com/80?text=Logo';
        }} data-id="element-702" />
        </div>
        <div data-id="element-703">
          <h3 className="text-2xl font-bold text-gray-900" data-id="element-704">{partner.name}</h3>
          <p className="text-sm text-gray-500" data-id="element-705">Partner Greenback</p>
        </div>
      </div>

      {/* Editable Fields */}
      <div data-id="element-706">
        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-707">
          Nome Partner
        </label>
        <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-708" />
      </div>

      <div className="grid grid-cols-2 gap-4" data-id="element-709">
        <div data-id="element-710">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-711">
            Email
          </label>
          <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-712" />
        </div>
        <div data-id="element-713">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-714">
            Website
          </label>
          <input type="url" name="website" value={formData.website} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-715" />
        </div>
      </div>

      <div data-id="element-716">
        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-717">
          Descrizione
        </label>
        <textarea name="description" value={formData.description} onChange={handleChange} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent resize-none" data-id="element-718" />
      </div>

      <div data-id="element-719">
        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-720">
          Numero Sedi
        </label>
        <input type="number" name="locations" value={formData.locations} onChange={handleChange} min="1" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-721" />
      </div>

      <div data-id="element-722">
        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-723">
          Stato
        </label>
        <select name="status" value={formData.status} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-724">
          <option value="active" data-id="element-725">Attivo</option>
          <option value="inactive" data-id="element-726">Inattivo</option>
          <option value="pending" data-id="element-727">In Attesa</option>
        </select>
      </div>

      {/* Categories */}
      <div data-id="element-728">
        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-729">
          Categorie
        </label>
        <div className="grid grid-cols-2 gap-3 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3" data-id="element-730">
          {allCategories.map(category => <label key={category} className="flex items-center gap-2 cursor-pointer" data-id="element-731">
              <input type="checkbox" checked={formData.categories.includes(category)} onChange={() => handleCategoryToggle(category)} className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-600" data-id="element-732" />
              <span className="text-sm text-gray-700" data-id="element-733">{category}</span>
            </label>)}
        </div>
      </div>

      {/* Read-only Stats */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-734">
        <h4 className="font-semibold text-gray-900 mb-3" data-id="element-735">Statistiche</h4>
        <div className="grid grid-cols-3 gap-4" data-id="element-736">
          <div className="text-center" data-id="element-737">
            <p className="text-2xl font-bold text-gray-900" data-id="element-738">
              {partner.activeRewards}
            </p>
            <p className="text-xs text-gray-500" data-id="element-739">Premi Attivi</p>
          </div>
          <div className="text-center" data-id="element-740">
            <p className="text-2xl font-bold text-gray-900" data-id="element-741">
              {partner.activeUsers.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500" data-id="element-742">Utenti Attivi</p>
          </div>
          <div className="text-center" data-id="element-743">
            <p className="text-2xl font-bold text-gray-900" data-id="element-744">
              {partner.totalRedemptions.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500" data-id="element-745">Riscatti Totali</p>
          </div>
        </div>
      </div>
    </BaseDrawer>;
};
export default PartnerDetailDrawer;